import React, { FC } from 'react'
import Skeleton from 'Components/DesignSystem/Skeleton/Skeleton'

/**
 * The product detail page's first-load skeleton, on the DS `Skeleton` primitive
 * (§ 62 grammar, adopted here in § 78): one announced sr-only status, `aria-hidden`
 * shapes, the shimmer lives in the primitive. Replaced 253 lines of hand-rolled
 * `tw-animate-pulse` grids — this file was one of the LEGACY_PULSE allowlist entries
 * in `skeletonGrammar.test.ts`, which shrinks with this change.
 *
 * The shapes mirror the loaded page: breadcrumb trail, title row with actions, then
 * the info card (image square + thumbnails beside a name/description/fields grid).
 */

const ATTRIBUTE_ROWS = 6;
const ROW_WIDTHS = ['55%', '70%', '60%'];

const ProductViewSkeleton: FC<{ 'data-testid'?: string }> = ({
  'data-testid': dataTestId,
}) => {
  const baseId = dataTestId || 'product-view-skeleton';
  return (
    <div aria-busy="true" data-testid={baseId}>
      <span role="status" className="tw-sr-only" data-testid={`${baseId}-status`}>
        Loading product
      </span>
      <div aria-hidden="true" data-testid={`${baseId}-shapes`}>
        {/* Breadcrumb trail */}
        <div className="tw-flex tw-items-center tw-gap-3">
          <Skeleton variant="text" width="5rem" />
          <Skeleton variant="text" width="6rem" />
          <Skeleton variant="text" width="8rem" />
        </div>

        {/* Title row: back + name on the left, actions on the right */}
        <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <Skeleton variant="circle" width="20px" height="20px" />
            <Skeleton variant="text" width="12rem" />
          </div>
          <Skeleton variant="rect" width="120px" height="36px" className="tw-rounded-ctl" />
        </div>

        {/* Info card: image column + details grid */}
        <div className="tw-mt-4 tw-flex tw-gap-6 tw-rounded-srf tw-border tw-border-edge tw-p-5">
          <div className="tw-flex-shrink-0">
            <Skeleton variant="rect" width="196px" height="196px" className="tw-rounded-srf" />
            <div className="tw-mt-2 tw-flex tw-gap-1">
              {ROW_WIDTHS.map((_, i) => (
                // Static layout, not data — the index is the only identity a placeholder has.
                // eslint-disable-next-line react/no-array-index-key
                <Skeleton key={i} variant="rect" width="35px" height="35px" className="tw-rounded-ctl" />
              ))}
            </div>
          </div>
          <div className="tw-flex-1">
            <Skeleton variant="text" width="14rem" />
            <Skeleton variant="text" width="60%" className="tw-mt-3" />
            <Skeleton variant="text" width="18rem" className="tw-mt-3" />
            <div className="tw-mt-5 tw-flex tw-flex-wrap">
              {Array.from({ length: ATTRIBUTE_ROWS }, (_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="tw-w-1/3 tw-p-1 tw-mt-3">
                  <Skeleton variant="text" width="6rem" />
                  <Skeleton variant="text" width={ROW_WIDTHS[i % ROW_WIDTHS.length]} className="tw-mt-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductViewSkeleton
